import { Routes, Route, Navigate } from 'react-router'
import { lazy, Suspense, useEffect } from 'react'
import { Spinner } from '@/components/ui/Spinner'
import { RouteErrorBoundary } from '@/components/ui/RouteErrorBoundary'
import { Toaster } from '@/components/ui/Toaster'
import { ScrollToTop } from '@/components/layout/ScrollToTop'
import { useAuthStore } from '@/stores/authStore'
import { track, FunnelEvent } from '@/lib/analytics'
import { captureError } from '@/lib/errorReporting'
import { CREATOR_REWARDS_ENABLED, MAKER_DIRECTORY_ENABLED } from '@/lib/flags'

import LandingPage from '@/routes/landing'
const RedeemPage = lazy(() => import('@/routes/auth/redeem'))
const LoginPage = lazy(() => import('@/routes/auth/login'))
const ForgotPasswordPage = lazy(() => import('@/routes/auth/forgot-password'))
const RegisterPage = lazy(() => import('@/routes/auth/register'))
const CampaignPage = lazy(() => import('@/routes/campaign'))
const FreeFilesPage = lazy(() => import('@/routes/free-files'))
const MakersPage = lazy(() => import('@/routes/makers'))
const MakerDetailPage = lazy(() => import('@/routes/maker-detail'))
const PricingPage = lazy(() => import('@/routes/pricing'))
const TermsPage = lazy(() => import('@/routes/terms'))
const PrivacyPage = lazy(() => import('@/routes/privacy'))
const AppLayout = lazy(() => import('@/routes/app/layout'))
const HomePage = lazy(() => import('@/routes/app/home'))
const ExplorePage = lazy(() => import('@/routes/app/explore'))
const TrailPage = lazy(() => import('@/routes/app/trail-detail'))
const LakePage = lazy(() => import('@/routes/app/lake-detail'))
const TerrainPage = lazy(() => import('@/routes/app/terrain'))
const GpxUploadPage = lazy(() => import('@/routes/app/gpx-upload'))
const JobPage = lazy(() => import('@/routes/app/job-detail'))
const AccountPage = lazy(() => import('@/routes/app/account'))
const AdminPage = lazy(() => import('@/routes/app/admin'))
const MakerEditorPage = lazy(() => import('@/routes/app/account/maker'))
const MakerModerationPage = lazy(() => import('@/routes/app/admin/makers'))
const RewardModerationPage = lazy(() => import('@/routes/app/admin/rewards'))
const ReviewsAdminPage = lazy(() => import('@/routes/app/admin/reviews'))
const NotFoundPage = lazy(() => import('@/routes/not-found'))

function PageFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

function AnimatedPage({ children }: { children: React.ReactNode }) {
  return <div className="animate-[fadeIn_200ms_ease-out]">{children}</div>
}

/**
 * Ships the Find a Maker surfaces dark until `VITE_MAKER_DIRECTORY === 'true'`
 * via the shared build-time flag. Public routes redirect home; the authed editor
 * and moderation routes redirect back into the app shell.
 */
function PublicMakerGate({ children }: { children: React.ReactNode }) {
  if (!MAKER_DIRECTORY_ENABLED) return <Navigate to="/" replace />
  return <>{children}</>
}

function AppMakerGate({ children }: { children: React.ReactNode }) {
  if (!MAKER_DIRECTORY_ENABLED) return <Navigate to="/app" replace />
  return <>{children}</>
}

/** Creator rewards ship dark until `VITE_CREATOR_REWARDS === 'true'`. */
function AppRewardsGate({ children }: { children: React.ReactNode }) {
  if (!CREATOR_REWARDS_ENABLED) return <Navigate to="/app" replace />
  return <>{children}</>
}

export function App() {
  useEffect(() => {
    useAuthStore.getState().initAuth()
    // O3: app-load funnel event (fires once on mount; PostHog also auto-captures
    // a pageview). No-op to PostHog when the key is unset.
    track(FunnelEvent.AppLoaded)
  }, [])

  return (
    <RouteErrorBoundary onError={(error) => captureError(error, 'route-boundary')}>
      {/* Reset scroll to top on navigation (Routes mode has no auto scroll reset). */}
      <ScrollToTop />
      {/* App-root toast outlet — rendered once, survives route changes (P3). */}
      <Toaster />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route
            path="/"
            element={
              <AnimatedPage>
                <LandingPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/auth/redeem"
            element={
              <AnimatedPage>
                <RedeemPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/auth/login"
            element={
              <AnimatedPage>
                <LoginPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/auth/forgot-password"
            element={
              <AnimatedPage>
                <ForgotPasswordPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/auth/register"
            element={
              <AnimatedPage>
                <RegisterPage />
              </AnimatedPage>
            }
          />
          {/* Short public entry point for "Start free trial" CTAs. Same page as
              /auth/register, pinned to its codeless free-trial mode so a code
              redeemed earlier in the tab can't turn a "start free trial" click
              into a coded signup. */}
          <Route
            path="/signup"
            element={
              <AnimatedPage>
                <RegisterPage forceCodeless />
              </AnimatedPage>
            }
          />
          <Route
            path="/free-files"
            element={
              <AnimatedPage>
                <FreeFilesPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/pricing"
            element={
              <AnimatedPage>
                <PricingPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/terms"
            element={
              <AnimatedPage>
                <TermsPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/privacy"
            element={
              <AnimatedPage>
                <PrivacyPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/campaign/:slug"
            element={
              <AnimatedPage>
                <CampaignPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/makers"
            element={
              <AnimatedPage>
                <PublicMakerGate>
                  <MakersPage />
                </PublicMakerGate>
              </AnimatedPage>
            }
          />
          <Route
            path="/makers/:makerId"
            element={
              <AnimatedPage>
                <PublicMakerGate>
                  <MakerDetailPage />
                </PublicMakerGate>
              </AnimatedPage>
            }
          />
          <Route
            path="/app"
            element={
              <AnimatedPage>
                <AppLayout />
              </AnimatedPage>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="explore" element={<ExplorePage />} />
            <Route path="search" element={<Navigate to="/app/explore" replace />} />
            <Route path="map" element={<Navigate to="/app/explore" replace />} />
            <Route path="trail/:trailId" element={<TrailPage />} />
            <Route path="lake/:lakeId" element={<LakePage />} />
            <Route path="terrain" element={<TerrainPage />} />
            <Route path="upload" element={<GpxUploadPage />} />
            <Route path="job/:jobId" element={<JobPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route
              path="account/maker"
              element={
                <AppMakerGate>
                  <MakerEditorPage />
                </AppMakerGate>
              }
            />
            <Route path="admin" element={<AdminPage />} />
            <Route
              path="admin/rewards"
              element={
                <AppRewardsGate>
                  <RewardModerationPage />
                </AppRewardsGate>
              }
            />
            <Route path="admin/reviews" element={<ReviewsAdminPage />} />
            <Route
              path="admin/makers"
              element={
                <AppMakerGate>
                  <MakerModerationPage />
                </AppMakerGate>
              }
            />
          </Route>
          <Route
            path="*"
            element={
              <AnimatedPage>
                <NotFoundPage />
              </AnimatedPage>
            }
          />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  )
}
